/**
 * Converts a name and numeric ID into a URL-friendly slug.
 * e.g. toSlug("Dublin", 123) → "dublin-123"
 */
export function toSlug(name: string, id: number): string {
  const slugified = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slugified}-${id}`;
}

/**
 * Extracts the numeric ID from the end of a slug.
 * e.g. extractIdFromSlug("dublin-123") → 123
 */
export function extractIdFromSlug(slug: string): number {
  const match = slug.match(/-(\d+)$/);
  if (!match) {
    return parseInt(slug, 10);
  }
  return parseInt(match[1], 10);
}

/**
 * Extracts a human-readable name from a slug by removing the trailing ID
 * and converting hyphens to title-cased words.
 * e.g. formatSlugName("premier-league-42") → "Premier League"
 */
export function formatSlugName(slug: string): string {
  return slug
    .replace(/-\d+$/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Converts a restaurant name into a URL-friendly slug without a numeric
 * suffix (restaurants are looked up by resOS id + slug, not by slug alone).
 * Normalises Unicode accents (NFD decomposition) so "Café" → "cafe".
 * e.g. toRestaurantSlug("Le Café de Paris") → "le-cafe-de-paris"
 */
export function toRestaurantSlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
